'use client'

import { useEffect, useRef } from 'react'
import { PDFDownloadLink, usePDF } from '@react-pdf/renderer'

import { useUserContext } from '@/context'
import { InlineErrorBoundary } from '@/components/errors/InlineErrorBoundary'
import { formatDocumentFilename } from '@/utils/helpers'
import type {
  PDFDownloaderProps,
  PDFDownloadTriggerProps,
} from '@/types/document'

const triggerDownload = (url: string, filename: string) => {
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
}

export const PDFDownloader = ({
  document,
  children,
  filename,
}: Readonly<PDFDownloaderProps>) => {
  const { isClient } = useUserContext()

  if (!isClient || !document) return null

  return (
    <InlineErrorBoundary label="Erreur lors de la génération du PDF">
      <PDFDownloadLink
        document={document}
        fileName={
          filename || formatDocumentFilename(undefined, undefined, undefined)
        }
      >
        {children}
      </PDFDownloadLink>
    </InlineErrorBoundary>
  )
}

export const PDFDownloadTrigger = ({
  document: doc,
  filename = 'document.pdf',
  triggerRef,
}: Readonly<PDFDownloadTriggerProps>) => {
  const [instance, updateInstance] = usePDF({ document: doc })
  const pendingTrigger = useRef(false)

  useEffect(() => {
    updateInstance(doc)
  }, [doc, updateInstance])

  useEffect(() => {
    if (!pendingTrigger.current || instance.loading || !instance.url) return
    pendingTrigger.current = false
    triggerDownload(instance.url, filename)
  }, [instance.loading, instance.url, filename])

  useEffect(() => {
    triggerRef.current = {
      triggerDownload: () => {
        if (instance.error) return
        if (!instance.loading && instance.url) {
          triggerDownload(instance.url, filename)
        } else {
          pendingTrigger.current = true
        }
      },
    }
    return () => {
      triggerRef.current = null
    }
  }, [instance.error, instance.loading, instance.url, filename, triggerRef])

  return null
}
